// Constants
const DEFAULT_SITE_NAME = "Youverse";
const DEFAULT_URL = "https://www.youverse.id";

// Cache
let cachedBaseUrl = null;

/**
 * Gets the base URL of the site, checking meta tag, window location, and fallback
 * @returns {string} The base URL without trailing slashes
 */
function getBaseUrl() {
  if (cachedBaseUrl) {
    return cachedBaseUrl;
  }

  if (typeof document !== "undefined") {
    const metaUrl = document.querySelector('meta[name="url"]')?.content;

    if (metaUrl) {
      cachedBaseUrl = metaUrl.replace(/\/+$/, "");
      return cachedBaseUrl;
    }
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    cachedBaseUrl = window.location.origin.replace(/\/+$/, "");
    return cachedBaseUrl;
  }

  return DEFAULT_URL;
}

/**
 * Clears the cached base URL (useful for testing)
 */
export function clearUrlCache() {
  cachedBaseUrl = null;
}

function getCurrentUrl() {
  if (typeof window === "undefined") {
    return getBaseUrl();
  }

  return window.location.href.split("#")[0];
}

function getLanguage() {
  if (typeof document !== "undefined") {
    return document.documentElement?.lang || "en";
  }

  return "en";
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getSiteName() {
  return (
    getMetaContent('meta[property="og:site_name"]') ||
    getMetaContent('meta[name="application-name"]') ||
    DEFAULT_SITE_NAME
  );
}

function getOrganizationId() {
  return `${getBaseUrl()}#organization`;
}

function getWebsiteId() {
  return `${getBaseUrl()}#website`;
}

function getMetaContent(selector) {
  if (typeof document === "undefined") {
    return "";
  }

  return document.querySelector(selector)?.content?.trim() || "";
}

function stripHtml(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeDateValue(value) {
  if (!value || typeof value !== "string") {
    return value;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
    return value.replace(" ", "T");
  }

  return value;
}

function compactValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => compactValue(entry))
      .filter(
        (entry) =>
          entry !== null &&
          entry !== undefined &&
          entry !== "" &&
          !(typeof entry === "object" && !Array.isArray(entry) && !Object.keys(entry).length),
      );
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((result, [key, entry]) => {
      const compacted = compactValue(entry);

      if (
        compacted === null ||
        compacted === undefined ||
        compacted === "" ||
        (Array.isArray(compacted) && !compacted.length) ||
        (typeof compacted === "object" &&
          !Array.isArray(compacted) &&
          !Object.keys(compacted).length)
      ) {
        return result;
      }

      result[key] = compacted;
      return result;
    }, {});
  }

  return value;
}

function buildOrganizationReference() {
  return {
    "@type": "Organization",
    "@id": getOrganizationId(),
  };
}

function buildWebsiteReference() {
  return {
    "@type": "WebSite",
    "@id": getWebsiteId(),
  };
}

export function toAbsoluteUrl(path) {
  if (!path) {
    return "";
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (path.startsWith("//")) {
    if (typeof window !== "undefined") {
      return `${window.location.protocol}${path}`;
    }

    return `https:${path}`;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${getBaseUrl()}${normalizedPath}`;
}

export function resolveMediaUrl(path, basePath = "") {
  if (!path) {
    return "";
  }

  if (/^https?:\/\//i.test(path) || path.startsWith("//")) {
    return path;
  }

  if (!basePath) {
    return path;
  }

  const normalizedBasePath = String(basePath).replace(/\/+$/, "");
  const normalizedPath = String(path).replace(/^\/+/, "");

  return `${normalizedBasePath}/${normalizedPath}`;
}

export function getPageTitle() {
  if (typeof document === "undefined") {
    return "";
  }

  const siteName = getSiteName();
  const siteSuffix = new RegExp(`\\s+\\|\\s+${escapeRegExp(siteName)}$`);

  return document.title.replace(siteSuffix, "").trim();
}

export function getPageDescription() {
  return getMetaContent('meta[name="description"]');
}

export function buildPageSchema(type, overrides = {}) {
  const currentUrl = getCurrentUrl();

  return compactValue({
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${currentUrl}#${String(type).toLowerCase()}`,
    name: getPageTitle(),
    url: currentUrl,
    description: getPageDescription(),
    inLanguage: getLanguage(),
    isPartOf: buildWebsiteReference(),
    about: buildOrganizationReference(),
    ...overrides,
  });
}

export function buildSoftwareApplicationSchema(overrides = {}) {
  const currentUrl = getCurrentUrl();

  return compactValue({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${currentUrl}#softwareapplication`,
    name: getPageTitle(),
    url: currentUrl,
    description: getPageDescription(),
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    inLanguage: getLanguage(),
    isPartOf: buildWebsiteReference(),
    applicationSuite: getSiteName(),
    publisher: buildOrganizationReference(),
    brand: buildOrganizationReference(),
    ...overrides,
  });
}

export function buildArticleSchema({
  type = "Article",
  title,
  description,
  url,
  image,
  datePublished,
  dateModified,
}) {
  const resolvedUrl = toAbsoluteUrl(url || getCurrentUrl());

  return compactValue({
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${resolvedUrl}#article`,
    headline: title,
    name: title,
    description: stripHtml(description),
    url: resolvedUrl,
    image: image ? [toAbsoluteUrl(image)] : undefined,
    datePublished: normalizeDateValue(datePublished),
    dateModified: normalizeDateValue(dateModified || datePublished),
    inLanguage: getLanguage(),
    isPartOf: buildWebsiteReference(),
    author: buildOrganizationReference(),
    publisher: buildOrganizationReference(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${resolvedUrl}#webpage`,
    },
  });
}

export function buildBookSchema({ title, description, url, image }) {
  const resolvedUrl = toAbsoluteUrl(url || getCurrentUrl());

  return compactValue({
    "@context": "https://schema.org",
    "@type": "Book",
    "@id": `${resolvedUrl}#book`,
    name: title,
    description: stripHtml(description),
    url: resolvedUrl,
    image: image ? [toAbsoluteUrl(image)] : undefined,
    inLanguage: getLanguage(),
    isPartOf: buildWebsiteReference(),
    author: buildOrganizationReference(),
    publisher: buildOrganizationReference(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${resolvedUrl}#webpage`,
    },
  });
}

export function buildCollectionPageSchema({ title, description, items = [] }) {
  const currentUrl = getCurrentUrl();

  return compactValue({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${currentUrl}#collectionpage`,
    name: title || getPageTitle(),
    description: description || getPageDescription(),
    url: currentUrl,
    inLanguage: getLanguage(),
    isPartOf: buildWebsiteReference(),
    mainEntity: items.length
      ? {
          "@type": "ItemList",
          itemListElement: items.map((item, index) =>
            compactValue({
              "@type": "ListItem",
              position: index + 1,
              url: toAbsoluteUrl(item.url),
              name: item.name,
            }),
          ),
        }
      : undefined,
  });
}

export function buildVideoObjectSchema({
  title,
  description,
  url,
  thumbnailUrl,
  uploadDate,
  duration,
  embedUrl,
}) {
  const resolvedUrl = toAbsoluteUrl(url || getCurrentUrl());

  return compactValue({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": `${resolvedUrl}#video`,
    name: title,
    description: stripHtml(description),
    url: resolvedUrl,
    thumbnailUrl: thumbnailUrl ? toAbsoluteUrl(thumbnailUrl) : undefined,
    uploadDate: normalizeDateValue(uploadDate),
    duration,
    embedUrl,
    inLanguage: getLanguage(),
    publisher: buildOrganizationReference(),
  });
}

export function buildFaqPageSchema({ title, description, faqs = [] }) {
  const currentUrl = getCurrentUrl();

  return compactValue({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${currentUrl}#faqpage`,
    name: title || getPageTitle(),
    description: description || getPageDescription(),
    url: currentUrl,
    inLanguage: getLanguage(),
    isPartOf: buildWebsiteReference(),
    mainEntity: faqs.map((faq) =>
      compactValue({
        "@type": "Question",
        name: stripHtml(faq.question),
        acceptedAnswer: {
          "@type": "Answer",
          text: stripHtml(faq.answer),
        },
      }),
    ),
  });
}

export function buildBreadcrumbSchema(items = []) {
  const breadcrumbItems = items.filter((item) => item && item.name && item.url);

  if (!breadcrumbItems.length) {
    return {};
  }

  return compactValue({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${getCurrentUrl()}#breadcrumb`,
    itemListElement: breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.url),
    })),
  });
}

function buildHomeBreadcrumb() {
  return { name: "Home", url: "/" };
}

function buildSectionBreadcrumb(name, url) {
  return [buildHomeBreadcrumb(), { name, url }];
}

export function buildPublicPageSchemas(componentName, pageProps = {}, currentTitle = "") {
  const title = currentTitle || getPageTitle();

  switch (componentName) {
    case "Home":
      return [
        buildPageSchema("WebPage", {
          name: title,
        }),
      ];
    case "Company":
      return [
        buildPageSchema("AboutPage", {
          name: title || "About Us",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("About Us", "/company")),
      ];
    case "ContactSales":
      return [
        buildPageSchema("ContactPage", {
          name: title || "Contact Sales",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("Contact Sales", "/contact-sales")),
      ];
    case "Faqs":
      return [
        buildPageSchema("WebPage", {
          name: title || "FAQ",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("FAQ", "/faq")),
      ];
    case "Pricing":
      return [
        buildPageSchema("WebPage", {
          name: title || "Pricing",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("Pricing", "/pricing")),
      ];
    case "Blog":
      return [buildBreadcrumbSchema(buildSectionBreadcrumb("Blog", "/resources/blog"))];
    case "Press":
      return [buildBreadcrumbSchema(buildSectionBreadcrumb("Press", "/resources/press"))];
    case "Projects":
      return [buildBreadcrumbSchema(buildSectionBreadcrumb("Projects", "/resources/projects"))];
    case "Ebooks":
      return [buildBreadcrumbSchema(buildSectionBreadcrumb("Ebooks", "/resources/ebooks"))];
    case "Videos":
      return [buildBreadcrumbSchema(buildSectionBreadcrumb("Videos", "/resources/videos"))];
    case "PostPage": {
      const categoryId = pageProps?.post?.category_id;
      const section =
        categoryId === 1
          ? { name: "Blog", url: "/resources/blog" }
          : categoryId === 2
            ? { name: "Press", url: "/resources/press" }
            : { name: "Projects", url: "/resources/projects" };

      return [
        buildBreadcrumbSchema([
          buildHomeBreadcrumb(),
          section,
          { name: pageProps?.post?.title || title, url: getCurrentUrl() },
        ]),
      ];
    }
    case "EbookPage":
      return [
        buildBreadcrumbSchema([
          buildHomeBreadcrumb(),
          { name: "Ebooks", url: "/resources/ebooks" },
          { name: pageProps?.ebook?.title || title, url: getCurrentUrl() },
        ]),
      ];
    case "UseCases/Banking":
      return [
        buildPageSchema("WebPage", {
          name: "Banking",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("Banking", "/industries/banking")),
      ];
    case "UseCases/Igaming":
      return [
        buildPageSchema("WebPage", {
          name: "iGaming",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("iGaming", "/industries/igaming")),
      ];
    case "UseCases/Hospitality":
      return [
        buildPageSchema("WebPage", {
          name: "Hospitality",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("Hospitality", "/industries/hospitality")),
      ];
    case "UseCases/Mobility":
      return [
        buildPageSchema("WebPage", {
          name: "Mobility",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("Mobility", "/industries/mobility")),
      ];
    case "Products/YouFace":
      return [
        buildSoftwareApplicationSchema({
          name: "YouFace",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("YouFace", "/products/youFace")),
      ];
    case "Products/YouAuth":
      return [
        buildSoftwareApplicationSchema({
          name: "YouAuth",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("YouAuth", "/products/youAuth")),
      ];
    case "Products/YouID":
      return [
        buildSoftwareApplicationSchema({
          name: "YouID",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("YouID", "/products/youID")),
      ];
    case "Products/YouLive":
      return [
        buildSoftwareApplicationSchema({
          name: "YouLive",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("YouLive", "/products/youlive")),
      ];
    case "Products/YouAge":
      return [
        buildSoftwareApplicationSchema({
          name: "YouAge",
        }),
        buildBreadcrumbSchema(buildSectionBreadcrumb("YouAge", "/products/youage")),
      ];
    default:
      return [];
  }
}
